import momentTz from 'moment-timezone';
import { buildTemplateSeries } from './providerTemplate';
import {
  getStaffMemberEventsV2,
  mapAvailabilityBlocksToEvents
} from './staff';
import { EVENT_STATUS } from 'utils/constants/status';
import { IClinicDayColumn, IClinicDayEvent } from 'model/ops';
import { DT_DISCIPLINE_ORDER } from './clinicDtCapacity';
import { formatProviderName } from 'utils/format/providerName';

/**
 * AXON-3585: shaping the clinic-wide day grid.
 *
 * One clinician per column, against one shared clock. Each column is folded by
 * exactly the rules the provider template uses — the same
 * mapAvailabilityBlocksToEvents for the standing slots and the same
 * buildTemplateSeries for the recurring clients — so a slot reads identically
 * whether you look at it on a person's profile or here.
 */

/** Slot states the grid distinguishes. */
export const SLOT_STATE = {
  held: 'held',
  notStarted: 'not-started',
  open: 'open',
  nonBookable: 'non-bookable'
} as const;

/**
 * Column order: disciplines as the tables order them, therapists before the
 * assistants reporting under them, then by name. The grid has to read as the
 * same subject as the table it was opened from.
 */
const columnRank = (column: IClinicDayColumn) => {
  const discipline = DT_DISCIPLINE_ORDER.indexOf(column.discipline);
  return [
    discipline === -1 ? DT_DISCIPLINE_ORDER.length : discipline,
    column.isAssistantGrade ? 1 : 0
  ];
};

export const orderColumns = (
  columns: IClinicDayColumn[]
): IClinicDayColumn[] =>
  [...(columns || [])].sort((a, b) => {
    const [aDiscipline, aGrade] = columnRank(a);
    const [bDiscipline, bGrade] = columnRank(b);
    if (aDiscipline !== bDiscipline) return aDiscipline - bDiscipline;
    if (aGrade !== bGrade) return aGrade - bGrade;
    return formatProviderName(a).localeCompare(formatProviderName(b));
  });

/**
 * How the URL says "nobody", as distinct from saying nothing at all.
 *
 * Unticking the last clinician is a choice, and an absent param is the absence
 * of one. Both cannot be the empty string, or clearing the picker silently
 * re-selects everyone.
 */
export const NO_CLINICIANS = 'none';

/**
 * AXON-3589: a comma-separated list of provider ids from the URL.
 *
 * Three outcomes, deliberately distinct:
 *   null — the param is absent. Nobody has narrowed anything; defaults stand.
 *   []   — the param says `none`. Somebody deselected everyone, and the grid
 *          should say so rather than quietly showing the lot.
 *   ids  — a usable list, in the order given.
 *
 * Junk entries are dropped rather than rejecting the whole list, and a list
 * with nothing usable left falls back to null: a mangled link is not a request
 * to show nobody. Membership is not checked here — see resolveSelection.
 */
export const parseIdList = (raw: string | null): number[] | null => {
  if (raw === null || raw === '') return null;
  if (raw.trim().toLowerCase() === NO_CLINICIANS) return [];
  const ids = raw
    .split(',')
    .map(part => Number(part.trim()))
    .filter(id => Number.isInteger(id) && id > 0);
  const unique = Array.from(new Set(ids));
  return unique.length ? unique : null;
};

/**
 * Narrow a selection from the URL to clinicians this clinic actually has.
 *
 * A link can name people who have since moved clinic or left, or carry an id
 * hand-edited to another clinic's. Filtering is what stops that drawing an
 * empty grid that blames the reader for a choice they did not make: a list
 * naming nobody who works here is a stale link, so the defaults stand. An
 * explicit `none` is preserved, because that one IS a choice.
 */
export const resolveSelection = (
  parsed: number[] | null,
  known: number[]
): number[] | null => {
  if (parsed === null) return null;
  if (parsed.length === 0) return [];
  const present = parsed.filter(id => known.includes(id));
  return present.length ? present : null;
};

/**
 * AXON-3585: the selection a discipline or grade in the URL names.
 *
 * The drill-down links here by what a figure MEANT — `?discipline=OT`,
 * `?specialty=COTA` — rather than by the ids that happened to make it up. A
 * shared link then still says "the COTAs at this clinic" a month later, when
 * someone has joined or left, and it reads as something a person can type.
 *
 * `specialty` wins where both are given: it is the narrower of the two, and a
 * grade always sits inside exactly one discipline.
 *
 * Matching nobody returns null rather than an empty grid — a grade this clinic
 * no longer staffs is a stale link, not a request to show nothing. That mirrors
 * `resolveSelection`, and for the same reason.
 */
export const selectionFromFilters = (
  columns: IClinicDayColumn[],
  discipline: string | null,
  specialty: string | null
): number[] | null => {
  const wanted = (specialty || discipline || '').trim().toLowerCase();
  if (!wanted) return null;

  const field = specialty
    ? (column: IClinicDayColumn) => column.specialtyAbbreviation
    : (column: IClinicDayColumn) => column.discipline;

  const ids = (columns || [])
    .filter(column => (field(column) || '').toLowerCase() === wanted)
    .map(column => column.providerId);

  return ids.length ? ids : null;
};

/**
 * The clinician columns react-big-calendar draws against the shared clock.
 *
 * Takes the order it is given rather than sorting again — `applyColumnOrder`
 * is the one place that decides what order means, so a hand-arranged grid is
 * not quietly re-sorted back to the default on the next render.
 */
export const toResources = (columns: IClinicDayColumn[]) =>
  (columns || []).map(column => ({
    id: column.providerId,
    title: formatProviderName(column)
  }));

/**
 * AXON-3585: the columns in the order they should be drawn.
 *
 * `order` is a hand-arranged list of provider ids — dragging a clinician
 * beside another is how two are compared, in place of highlighting the times
 * they happen to share. Null means nobody has rearranged anything, so the
 * default discipline order stands.
 *
 * A clinician missing from a stale `order` falls to the end in default order
 * rather than disappearing: the arrangement is a preference, and a preference
 * must never be able to hide someone.
 */
export const applyColumnOrder = (
  columns: IClinicDayColumn[],
  order: number[] | null
): IClinicDayColumn[] => {
  const byDefault = orderColumns(columns);
  if (!order || order.length === 0) return byDefault;

  const ranked = order
    .map(id => byDefault.find(column => column.providerId === id))
    .filter((column): column is IClinicDayColumn => !!column);

  const rest = byDefault.filter(column => !order.includes(column.providerId));
  return [...ranked, ...rest];
};

/** Move one column to a new position, as a drag from one index to another. */
export const moveColumn = (
  order: number[],
  from: number,
  to: number
): number[] => {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= order.length ||
    to >= order.length
  ) {
    return order;
  }
  const next = [...order];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

const startOfCurrentWeek = () =>
  momentTz()
    .startOf('week')
    .toDate();

/**
 * The end of the window the backend sends: this week plus the six looked ahead.
 *
 * Mirrors SERIES_LOOK_AHEAD_WEEKS on the server. A series that has not started
 * yet still holds its slot, and it only appears at all because of that window —
 * so the range handed to the shared mapper has to cover it, or the filter drops
 * exactly the events this grid went looking for.
 */
const SERIES_LOOK_AHEAD_WEEKS = 6;

const lookAheadEnd = (weekStart: Date) =>
  momentTz(weekStart)
    .add(SERIES_LOOK_AHEAD_WEEKS + 1, 'weeks')
    .toDate();

/**
 * The events of one column, mapped EXACTLY as the provider profile maps its
 * own recurring template: one call to getStaffMemberEventsV2, which is where
 * a bar's colour, its chip label and the client's name are decided — from the
 * PARENT appointment type where there is one, which is what makes an OT
 * session read as "OT" rather than as the "45" of its child type.
 *
 * Nothing about how a slot looks is decided here, so a change to the provider
 * template shows up on this grid without anybody copying it across.
 *
 * Two things this adds, both about the grid rather than about appearance:
 *  - the zone to read dates in, which the profile takes from the event and
 *    this view knows from the one clinic every column belongs to;
 *  - instanceIndex, from the series start the backend reads off the master
 *    row. buildTemplateSeries uses it to tell a series that has not begun yet
 *    from one already running, and the profile gets it from a raw column this
 *    query cannot select.
 */
const toTemplateEvents = (
  events: IClinicDayEvent[],
  range: { start: Date; end: Date },
  clinicTimezone?: string
) =>
  getStaffMemberEventsV2(events || [], range, clinicTimezone).map(
    (event: any) => {
      const start = new Date(event.startDate).getTime();
      const seriesStart = new Date(
        event.masterStartDate ?? event.startDate
      ).getTime();
      return {
        ...event,
        clinic: clinicTimezone ? { timezone: clinicTimezone } : event.clinic,
        instanceIndex: start === seriesStart ? 0 : 1
      };
    }
  );

/** Whether a folded slot is a client's, and whether that client has started. */
const stateOf = (event: any) => {
  if (event.type === 'availability') {
    return event.availType === 'working' || event.title === 'Open Care Session'
      ? SLOT_STATE.open
      : SLOT_STATE.nonBookable;
  }
  return event.seriesStartDate ? SLOT_STATE.notStarted : SLOT_STATE.held;
};

/**
 * Every column's slots for one weekday, tagged with the clinician they belong
 * to so react-big-calendar can place them in that column.
 *
 * A care session that a series holds is dropped from the open slots: the
 * template says a slot exists, the series says it is taken, and drawing both
 * would show an opening on top of a booked client.
 */
export const buildDayEvents = (
  columns: IClinicDayColumn[],
  weekday: number,
  clinicTimezone?: string
): any[] => {
  const weekStart = startOfCurrentWeek();
  const onWeekday = (date: Date) => momentTz(date).day() === weekday;

  return orderColumns(columns).flatMap(column => {
    // Told it is a template, so the labels are the week view's: only a care
    // session carries the word "Open", and every other block reads as its own
    // name rather than being announced as an opening the view does not track.
    const slots = (mapAvailabilityBlocksToEvents(
      column.availabilities || [],
      [],
      weekStart,
      true
    ) || []) as any[];

    const series = buildTemplateSeries(
      toTemplateEvents(
        (column.events || [])
          // A cancelled instance of a live series still holds the slot next
          // week; a deleted one frees it. The week view drops deleted
          // occurrences, and a held bar drawn over a slot that is open again is
          // the grid under-reporting capacity.
          .filter(event => event.status !== EVENT_STATUS.deleted),
        // The whole look-ahead, not just the displayed week: the shared mapper
        // filters to the range it is given, and a series that has not started
        // yet lies outside this week by definition.
        { start: weekStart, end: lookAheadEnd(weekStart) },
        clinicTimezone
      ) as any[],
      weekStart
    ).filter(event => onWeekday(new Date(event.start!)));

    // A slot a series already holds is not also an opening. Matched by overlap
    // rather than by an identical start: a real appointment can sit a few
    // minutes off the template slot it occupies, and an exact-time comparison
    // would then draw an opening on top of a booked client — the one mistake
    // this grid must not make.
    const taken = series.map(event => ({
      start: new Date(event.start!).getTime(),
      end: new Date(event.end!).getTime()
    }));
    const overlapsSomethingTaken = (slot: any) => {
      const start = new Date(slot.start).getTime();
      const end = new Date(slot.end).getTime();
      return taken.some(held => held.start < end && start < held.end);
    };
    const freeSlots = slots.filter(
      slot => onWeekday(new Date(slot.start)) && !overlapsSomethingTaken(slot)
    );

    return [...series, ...freeSlots].map(event => ({
      ...event,
      resourceId: column.providerId,
      slotState: stateOf(event)
    }));
  });
};

/** Booked over target, for the column header pills. */
export const headerFigures = (column: IClinicDayColumn) => ({
  day: { booked: column.dayBooked, target: column.dayTarget },
  week: { booked: column.weekBooked, target: column.weekTarget }
});

/** Short of target reads differently from met — the provider profile's rule. */
export const isShort = (booked: number, target: number) =>
  target > 0 && booked < target;

/* ---------- which clinicians are loaded ---------- */

/**
 * The picker's groups: one per discipline present, in the tables' order.
 *
 * A discipline group holds its assistant grades — "All SLP" selects the SLPAs
 * too, because the filter names the discipline rather than the licensed grade
 * (settled 17 Aug 2026). The data already carries that: every clinician's
 * `discipline` is the parent, whatever their own grade.
 */
export const groupByDiscipline = (columns: IClinicDayColumn[]) => {
  const ordered = orderColumns(columns);
  return DT_DISCIPLINE_ORDER.map(discipline => ({
    discipline,
    members: ordered.filter(column => column.discipline === discipline)
  })).filter(group => group.members.length > 0);
};

export type GroupState = 'all' | 'some' | 'none';

/**
 * Whether a discipline is wholly, partly or not loaded. "Partly" has to be
 * distinguishable from "none" or the control lies about what is on screen.
 */
export const groupStateOf = (
  memberIds: number[],
  selected: number[]
): GroupState => {
  const chosen = memberIds.filter(id => selected.includes(id)).length;
  if (chosen === 0) return 'none';
  return chosen === memberIds.length ? 'all' : 'some';
};

/** Toggling a discipline takes or drops all of its members together. */
export const toggleGroup = (
  selected: number[],
  memberIds: number[]
): number[] => {
  const state = groupStateOf(memberIds, selected);
  // Partly selected reads as "not yet all of them", so it fills rather than
  // empties — the same way a tri-state checkbox behaves everywhere else.
  if (state === 'all') {
    return selected.filter(id => !memberIds.includes(id));
  }
  return Array.from(new Set([...selected, ...memberIds]));
};

export const toggleClinician = (
  selected: number[],
  providerId: number
): number[] =>
  selected.includes(providerId)
    ? selected.filter(id => id !== providerId)
    : [...selected, providerId];

/**
 * Search matches a clinician's name or their discipline, so "SLP" finds the
 * whole discipline and "bell" finds one person.
 */
export const matchesSearch = (column: IClinicDayColumn, term: string) => {
  const needle = (term || '').trim().toLowerCase();
  if (!needle) return true;
  return (
    // The list shows the Display Name, but the legal name in both orders still
    // finds the person: a surname is the most natural thing to type.
    formatProviderName(column).toLowerCase().includes(needle) ||
    `${column.firstName} ${column.lastName}`.toLowerCase().includes(needle) ||
    `${column.lastName} ${column.firstName}`.toLowerCase().includes(needle) ||
    column.discipline.toLowerCase().includes(needle) ||
    column.specialtyAbbreviation.toLowerCase().includes(needle)
  );
};

/**
 * The control names what it does, and adds the count once it is narrowing
 * something — so the state is legible without opening it, but the label does
 * not flip between a name and a number.
 */
export const selectionLabel = (selectedCount: number, total: number) => {
  if (total > 0 && selectedCount === total) return 'Filter by Specialty';
  return `Filter by Specialty (${selectedCount} of ${total})`;
};
